import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');
    if (!token) {
      setError('Reset token is missing. Please use the link from your email.');
      return;
    }
    const hasPassword = password.length > 0 || confirmPassword.length > 0;
    const hasPin = pin.length > 0 || confirmPin.length > 0;
    if (!hasPassword && !hasPin) {
      setError('Enter a new password and/or 6-digit PIN.');
      return;
    }
    if (hasPassword) {
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }
    if (hasPin) {
      if (!/^\d{6}$/.test(pin)) {
        setError('PIN must be a 6 digit number.');
        return;
      }
      if (pin !== confirmPin) {
        setError('PIN values do not match.');
        return;
      }
    }
    try {
      setSubmitting(true);
      await authService.resetPassword({ token, password: hasPassword ? password : undefined, pin: hasPin ? pin : undefined });
      setStatus('Credentials updated successfully. Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-4">Set a New Password</h2>
        {!token && (
          <p className="text-sm text-red-600 mb-4">
            A valid reset token is required. Please use the link sent to your email.
          </p>
        )}
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        {status && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{status}</p>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPassword">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              minLength={6}
              disabled={!token}
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              minLength={6}
              disabled={!token}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPin">
              New 6-digit PIN (optional)
            </label>
            <input
              type="password"
              id="newPin"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D+/g, '').slice(0, 6))}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              pattern="[0-9]{6}"
              inputMode="numeric"
              disabled={!token}
            />
          </div>
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPin">
              Confirm PIN
            </label>
            <input
              type="password"
              id="confirmPin"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D+/g, '').slice(0, 6))}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              pattern="[0-9]{6}"
              inputMode="numeric"
              disabled={!token}
            />
          </div>
          <button
            type="submit"
            disabled={!token || submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
          >
            {submitting ? 'Updating...' : 'Update Password'}
          </button>
        </form>
        <p className="text-center text-gray-600 text-xs mt-4">
          Remembered your password?{' '}
          <Link to="/login" className="text-blue-500 hover:text-blue-700">
            Back to login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
